import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Stack, Typography, Button, Chip, Avatar, Alert, Divider,
} from '@mui/material';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import GroupsIcon from '@mui/icons-material/Groups';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import { searchPatients } from '../api/patientApi';
import { getPendingMembers } from '../api/saasApi';
import { useMe } from '../context/MeContext';
import { useSnackbar } from '../components/Snackbar';
import { PageHeader, SectionCard, StatTile, EmptyState, CardsSkeleton } from '../components/ui';

const initials = (name = '') => {
  const parts = String(name).trim().split(' ').filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
};

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
};

/**
 * The landing page after sign-in. Previously login dropped the clinician
 * straight onto a raw patient table, which answers "who exists" but not
 * "what should I do now" — the question people actually arrive with.
 */
const Dashboard = () => {
  const navigate = useNavigate();
  const { me, can } = useMe();
  const { showSnackbar } = useSnackbar();

  // Newest patients for the "Recent patients" card, plus the clinic's real
  // patient count. Both used to come from a list capped at the oldest ~1000
  // patients, so the count stalled at 1001 and "recent" wasn't recent.
  const [recent, setRecent] = useState([]);
  const [patientTotal, setPatientTotal] = useState(0);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);

  const canApprove = can('user:approve') && !!me?.tenant;

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const requests = [searchPatients({ limit: 6, offset: 0, sortField: 'id', sortDirection: 'desc' })];
        if (canApprove) requests.push(getPendingMembers());

        const [p, q] = await Promise.all(requests);
        if (cancelled) return;

        const result = p.data?.succeeded ? p.data.data || {} : {};
        setRecent(result.items || []);
        setPatientTotal(result.total || 0);
        setPending(q ? q.data?.data || [] : []);
      } catch (e) {
        if (!cancelled) {
          setRecent([]);
          setPatientTotal(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [canApprove]);

  const copyJoinCode = async () => {
    try {
      await navigator.clipboard.writeText(me.joinCode);
      showSnackbar('Clinic code copied.', 'success');
    } catch (e) {
      showSnackbar('Could not copy — open Clinic settings to see the code.', 'warning');
    }
  };

  const isNewClinic = !loading && patientTotal === 0;

  return (
    <>
      <PageHeader
        title={`${greeting()}${me?.name ? `, ${me.name.split(' ')[0]}` : ''}`}
        subtitle={me?.tenant?.name}
        actions={
          <Button variant="contained" startIcon={<PersonAddAlt1Icon />} onClick={() => navigate('/patient')}>
            Add patient
          </Button>
        }
      />

      <Stack spacing={2}>
        {/* First-run guidance. A brand-new clinic sees this instead of three zeroes. */}
        {isNewClinic && (
          <Alert
            severity="info"
            action={
              <Button size="small" onClick={() => navigate('/patient')}>Add a patient</Button>
            }
          >
            <Typography variant="subtitle2">Welcome to Vqura</Typography>
            <Typography variant="body2">
              Add your first patient, run an assessment across the developmental domains, then
              export a progress report to share with their family.
            </Typography>
          </Alert>
        )}

        {loading ? (
          <CardsSkeleton count={3} />
        ) : (
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' } }}>
            <StatTile
              label="Patients"
              value={patientTotal}
              hint="in this clinic"
              icon={<GroupsIcon fontSize="small" />}
              onClick={() => navigate('/patient')}
            />
            {canApprove && (
              <StatTile
                label="Join requests"
                value={pending.length}
                hint={pending.length ? 'waiting on you' : 'nothing waiting'}
                tone={pending.length ? 'warning.main' : undefined}
                icon={<PendingActionsIcon fontSize="small" />}
                onClick={() => navigate('/users')}
              />
            )}
            {me?.billing && !me.billing.isExempt && (
              <StatTile
                label="Per assessment"
                value={`${me.billing.currency || 'INR'} ${me.billing.assessmentPrice}`}
                hint="price"
                icon={<EventRepeatIcon fontSize="small" />}
                onClick={can('billing:view') ? () => navigate('/billing') : undefined}
              />
            )}
          </Box>
        )}

        {/* Admins get their queue surfaced here rather than having to go looking. */}
        {canApprove && pending.length > 0 && (
          <SectionCard
            title={`${pending.length} ${pending.length === 1 ? 'person is' : 'people are'} waiting to join`}
            subtitle="They have no access to patients until you approve them"
            action={<Button size="small" variant="contained" onClick={() => navigate('/users')}>Review</Button>}
          >
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {pending.slice(0, 5).map((p) => (
                <Chip key={p.id} avatar={<Avatar>{initials(p.name)}</Avatar>} label={p.name || p.email} />
              ))}
              {pending.length > 5 && <Chip label={`+${pending.length - 5} more`} variant="outlined" />}
            </Stack>
          </SectionCard>
        )}

        <SectionCard
          title="Recent patients"
          subtitle="Jump back into an assessment"
          action={<Button size="small" onClick={() => navigate('/patient')}>View all</Button>}
        >
          {loading ? (
            <CardsSkeleton count={3} height={72} />
          ) : recent.length === 0 ? (
            <EmptyState
              variant="inline"
              icon={<GroupsIcon />}
              title="No patients yet"
              body="Add your first patient to begin an assessment."
              action={
                <Button variant="contained" startIcon={<PersonAddAlt1Icon />} onClick={() => navigate('/patient')}>
                  Add patient
                </Button>
              }
            />
          ) : (
            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' } }}>
              {recent.map((p) => (
                <Stack
                  key={p.id}
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  onClick={() => navigate(`/patient/${p.id}`)}
                  sx={{
                    p: 1.5, borderRadius: 1, cursor: 'pointer',
                    border: '1px solid', borderColor: 'divider',
                    '&:hover': { borderColor: 'primary.light', bgcolor: 'action.selected' },
                  }}
                >
                  <Avatar sx={{ width: 38, height: 38, bgcolor: 'grey.100', color: 'text.secondary', fontSize: 13, fontWeight: 700 }}>
                    {initials(p.name)}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" noWrap>{p.name || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap display="block">
                      {p.mobileNo || `ID ${p.id}`}
                    </Typography>
                  </Box>
                </Stack>
              ))}
            </Box>
          )}
        </SectionCard>

        {/* Growing the clinic is an admin job; put the code where they'll see it. */}
        {can('tenant:settings') && me?.tenant && (
          <SectionCard title="Invite your colleagues" dense>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                Share your clinic code and approve people as they request to join.
              </Typography>
              <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
              <Stack direction="row" spacing={1}>
                {me.joinCode && (
                  <Button size="small" variant="outlined" startIcon={<ContentCopyIcon />} onClick={copyJoinCode}>
                    {me.joinCode}
                  </Button>
                )}
                <Button size="small" onClick={() => navigate('/clinic')}>Clinic settings</Button>
              </Stack>
            </Stack>
          </SectionCard>
        )}
      </Stack>
    </>
  );
};

export default Dashboard;
